import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

export interface ProductMenuPage<TItem> {
    items: TItem[];
    endCursor: string | null;
    hasNextPage: boolean;
}

export interface ProductMenuGroup<TOption> {
    label: string;
    options: TOption[];
}

export const paginatedProductMenuStyles = {
    group: (
        base: Record<string, unknown>,
        {data}: {data: ProductMenuGroup<unknown>}
    ): Record<string, unknown> =>
        data.label === 'All products'
            ? {...base, borderTop: '1px solid var(--off-white-dk)'}
            : base,
};

interface PageState<TItem> extends ProductMenuPage<TItem> {
    isLoading: boolean;
    isLoadingNextPage: boolean;
    error: unknown | null;
    nextPageError: unknown | null;
    hasLoaded: boolean;
}

interface Options<TItem, TOption> {
    contextKey: string;
    minimumSearchLength: number;
    initialMenuIsOpen?: boolean;
    getRecommendations: () => Promise<TItem[]>;
    getPage: (search: string | null, after?: string | null) => Promise<ProductMenuPage<TItem>>;
    getItemId: (item: TItem) => string;
    toOptions: (items: TItem[]) => TOption[];
}

export interface PaginatedProductMenuState<TOption> {
    groups: ProductMenuGroup<TOption>[];
    inputValue: string;
    isLoading: boolean;
    isLoadingNextPage: boolean;
    recommendationError: unknown | null;
    pageError: unknown | null;
    nextPageError: unknown | null;
    onInputChange: (inputValue: string) => string;
    onMenuOpen: () => void;
    onMenuClose: () => void;
    onMenuScrollToBottom: () => void;
    retryRecommendations: () => void;
    retryPage: () => void;
}

const PRODUCT_SEARCH_DEBOUNCE_MS = 250;

function emptyPageState<TItem>(): PageState<TItem> {
    return {
        items: [],
        endCursor: null,
        hasNextPage: false,
        isLoading: false,
        isLoadingNextPage: false,
        error: null,
        nextPageError: null,
        hasLoaded: false,
    };
}

function mergeItems<TItem>(
    currentItems: TItem[],
    nextItems: TItem[],
    getItemId: (item: TItem) => string
): TItem[] {
    const itemsById = new Map<string, TItem>();
    currentItems.forEach((item) => itemsById.set(getItemId(item), item));
    nextItems.forEach((item) => itemsById.set(getItemId(item), item));

    return Array.from(itemsById.values());
}

export function usePaginatedProductMenu<TItem, TOption>({
    contextKey,
    minimumSearchLength,
    initialMenuIsOpen = false,
    getRecommendations,
    getPage,
    getItemId,
    toOptions,
}: Options<TItem, TOption>): PaginatedProductMenuState<TOption> {
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(initialMenuIsOpen);
    const [inputValue, setInputValue] = useState<string>('');
    const [recommendations, setRecommendations] = useState<TItem[]>([]);
    const [isLoadingRecommendations, setIsLoadingRecommendations] = useState<boolean>(false);
    const [recommendationError, setRecommendationError] = useState<unknown | null>(null);
    const [catalog, setCatalog] = useState<PageState<TItem>>(() => emptyPageState<TItem>());
    const [searchResults, setSearchResults] = useState<PageState<TItem>>(() =>
        emptyPageState<TItem>()
    );
    const recommendationSequence = useRef<number>(0);
    const catalogSequence = useRef<number>(0);
    const searchSequence = useRef<number>(0);
    const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const previousContextKey = useRef<string>(contextKey);
    const recommendationStatus = useRef<'idle' | 'loading' | 'loaded'>('idle');
    const catalogStatus = useRef<'idle' | 'loading' | 'loaded'>('idle');
    const activePageRef = useRef<PageState<TItem> | null>(null);
    const nextPageRequest = useRef<{sequence: number; cursor: string | null} | null>(null);
    const trimmedInput = inputValue.trim();
    const isBlank = trimmedInput === '';
    const isSearchReady = trimmedInput.length >= minimumSearchLength;

    const reset = useCallback(() => {
        recommendationSequence.current += 1;
        catalogSequence.current += 1;
        searchSequence.current += 1;
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        searchTimeout.current = null;
        recommendationStatus.current = 'idle';
        catalogStatus.current = 'idle';
        setInputValue('');
        setRecommendations([]);
        setIsLoadingRecommendations(false);
        setRecommendationError(null);
        setCatalog(emptyPageState<TItem>());
        setSearchResults(emptyPageState<TItem>());
    }, []);

    useEffect(
        () => () => {
            recommendationSequence.current += 1;
            catalogSequence.current += 1;
            searchSequence.current += 1;
            if (searchTimeout.current) clearTimeout(searchTimeout.current);
        },
        []
    );
    const loadRecommendations = useCallback(() => {
        const sequence = ++recommendationSequence.current;
        recommendationStatus.current = 'loading';
        setIsLoadingRecommendations(true);
        setRecommendationError(null);
        getRecommendations()
            .then((items) => {
                if (sequence !== recommendationSequence.current) return;
                setRecommendations(items);
                recommendationStatus.current = 'loaded';
            })
            .catch((error: unknown) => {
                if (sequence !== recommendationSequence.current) return;
                setRecommendationError(error);
                recommendationStatus.current = 'loaded';
            })
            .finally(() => {
                if (sequence === recommendationSequence.current) {
                    setIsLoadingRecommendations(false);
                }
            });
    }, [getRecommendations]);

    const loadFirstPage = useCallback(
        (search: string | null) => {
            const sequenceRef = search === null ? catalogSequence : searchSequence;
            const sequence = ++sequenceRef.current;
            const setState = search === null ? setCatalog : setSearchResults;
            if (search === null) catalogStatus.current = 'loading';
            setState({...emptyPageState<TItem>(), isLoading: true});

            getPage(search)
                .then((page) => {
                    if (sequence !== sequenceRef.current) return;
                    setState({
                        ...page,
                        isLoading: false,
                        isLoadingNextPage: false,
                        error: null,
                        nextPageError: null,
                        hasLoaded: true,
                    });
                    if (search === null) catalogStatus.current = 'loaded';
                })
                .catch((error: unknown) => {
                    if (sequence !== sequenceRef.current) return;
                    setState({
                        ...emptyPageState<TItem>(),
                        error,
                        hasLoaded: true,
                    });
                    if (search === null) catalogStatus.current = 'loaded';
                });
        },
        [getPage]
    );

    useEffect(() => {
        if (!isMenuOpen || !isBlank) return;
        if (recommendationStatus.current === 'idle') loadRecommendations();
        if (catalogStatus.current === 'idle') loadFirstPage(null);
    }, [isBlank, isMenuOpen, loadFirstPage, loadRecommendations]);

    useEffect(() => {
        if (previousContextKey.current === contextKey) return;
        previousContextKey.current = contextKey;
        reset();
        if (isMenuOpen) {
            loadRecommendations();
            loadFirstPage(null);
        }
    }, [contextKey, isMenuOpen, loadFirstPage, loadRecommendations, reset]);

    const activePage = isBlank ? catalog : searchResults;
    activePageRef.current = activePage;
    const loadNextPage = useCallback(
        (isRetry: boolean) => {
            const currentPage = activePageRef.current;
            if (
                !isMenuOpen ||
                (!isBlank && !isSearchReady) ||
                !currentPage ||
                currentPage.isLoading ||
                currentPage.isLoadingNextPage ||
                (currentPage.nextPageError && !isRetry) ||
                !currentPage.hasNextPage
            ) {
                return;
            }

            const sequenceRef = isBlank ? catalogSequence : searchSequence;
            const sequence = sequenceRef.current;
            const search = isBlank ? null : trimmedInput;
            const setState = isBlank ? setCatalog : setSearchResults;
            const cursor = currentPage.endCursor;
            if (
                nextPageRequest.current?.sequence === sequence &&
                nextPageRequest.current.cursor === cursor
            ) {
                return;
            }
            nextPageRequest.current = {sequence, cursor};
            setState((state) => ({...state, isLoadingNextPage: true, nextPageError: null}));
            getPage(search, cursor)
                .then((page) => {
                    if (sequence !== sequenceRef.current) return;
                    setState((state) => ({
                        ...state,
                        items: mergeItems(state.items, page.items, getItemId),
                        endCursor: page.endCursor,
                        hasNextPage: page.hasNextPage,
                        isLoadingNextPage: false,
                        nextPageError: null,
                    }));
                })
                .catch((error: unknown) => {
                    if (sequence !== sequenceRef.current) return;
                    setState((state) => ({
                        ...state,
                        isLoadingNextPage: false,
                        nextPageError: error,
                    }));
                })
                .finally(() => {
                    if (
                        nextPageRequest.current?.sequence === sequence &&
                        nextPageRequest.current.cursor === cursor
                    ) {
                        nextPageRequest.current = null;
                    }
                });
        },
        [getItemId, getPage, isBlank, isMenuOpen, isSearchReady, trimmedInput]
    );

    const groups = useMemo<ProductMenuGroup<TOption>[]>(() => {
        if (!isBlank) {
            if (!isSearchReady) return [];
            const searchOptions = toOptions(searchResults.items);

            return searchOptions.length ? [{label: 'Search results', options: searchOptions}] : [];
        }

        const blankGroups: ProductMenuGroup<TOption>[] = [];
        const recommendationOptions = toOptions(recommendations);
        if (recommendationOptions.length) {
            blankGroups.push({label: 'Recommended', options: recommendationOptions});
        }
        const catalogOptions = toOptions(catalog.items);
        if (catalogOptions.length) {
            blankGroups.push({label: 'All products', options: catalogOptions});
        }

        return blankGroups;
    }, [catalog.items, isBlank, isSearchReady, recommendations, searchResults.items, toOptions]);

    return {
        groups,
        inputValue,
        isLoading:
            activePage.isLoading || (isBlank && isLoadingRecommendations && groups.length === 0),
        isLoadingNextPage: activePage.isLoadingNextPage,
        recommendationError,
        pageError: activePage.error,
        nextPageError: activePage.nextPageError,
        onInputChange: (nextInputValue: string) => {
            setInputValue(nextInputValue);
            searchSequence.current += 1;
            if (searchTimeout.current) clearTimeout(searchTimeout.current);
            searchTimeout.current = null;
            setSearchResults(emptyPageState<TItem>());

            if (!isMenuOpen) return nextInputValue;

            const nextSearch = nextInputValue.trim();
            if (nextSearch === '') {
                if (recommendationStatus.current === 'idle') loadRecommendations();
                if (catalogStatus.current === 'idle') loadFirstPage(null);
                return nextInputValue;
            }

            if (nextSearch.length >= minimumSearchLength) {
                searchTimeout.current = setTimeout(() => {
                    searchTimeout.current = null;
                    loadFirstPage(nextSearch);
                }, PRODUCT_SEARCH_DEBOUNCE_MS);
            }

            return nextInputValue;
        },
        onMenuOpen: () => {
            setIsMenuOpen(true);
            if (isBlank) {
                if (recommendationStatus.current === 'idle') loadRecommendations();
                if (catalogStatus.current === 'idle') loadFirstPage(null);
            } else if (isSearchReady) {
                searchSequence.current += 1;
                if (searchTimeout.current) clearTimeout(searchTimeout.current);
                searchTimeout.current = setTimeout(() => {
                    searchTimeout.current = null;
                    loadFirstPage(trimmedInput);
                }, PRODUCT_SEARCH_DEBOUNCE_MS);
            }
        },
        onMenuClose: () => setIsMenuOpen(false),
        onMenuScrollToBottom: () => loadNextPage(false),
        retryRecommendations: loadRecommendations,
        retryPage: () => {
            if (activePage.nextPageError && activePage.items.length) {
                loadNextPage(true);
                return;
            }
            loadFirstPage(isBlank ? null : trimmedInput);
        },
    };
}
